import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import { ShieldCheck, HeartHandshake, Target, Lightbulb, Award, ArrowRight } from "lucide-react";
import { images, aboutValues } from "../mock";

const iconMap = { ShieldCheck, HeartHandshake, Target, Lightbulb, Award };

export default function About() {
  return (
    <main>
      <PageHero
        eyebrow="About Us"
        title="Audit Informed NDIS Compliance Consultants"
        subtitle="Practical compliance support for Australian providers — informed by audit experience, focused on implementation."
        breadcrumb={[{ label: "About Us" }]}
      />

      <section className="py-20 md:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-5 -left-5 w-32 h-32 bg-teal-soft rounded-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src={images.team} alt="Our team" loading="lazy" decoding="async" className="w-full h-[460px] object-cover" />
              </div>
              <div className="absolute -bottom-6 right-2 sm:-right-6 bg-navy text-white rounded-2xl p-5 shadow-xl max-w-[240px] sm:max-w-[280px]">
                <Award className="text-teal-mid" size={26} />
                <p className="mt-2 text-base font-bold text-white">Audit Informed Support</p>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                  Practical NDIS compliance guidance informed by audit experience and provider evidence expectations.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Who we are"
              title="An audit informed consultancy, not a generic template provider."
            />
            <div className="mt-6 space-y-4 text-slate-600 text-[16px] leading-relaxed">
              <p>
                Apex Quality Consultancy supports new and existing NDIS providers preparing for
                verification, certification, Stage 1, Stage 2, mid-term, renewal and corrective
                action processes.
              </p>
              <p>
                Our work is shaped by practical audit experience, the NDIS Practice Standards and a
                clear understanding of what providers need to implement to be audit ready.
              </p>
            </div>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Tailored policies, procedures, forms and registers",
                "Self-assessment and registration support",
                "Internal audits and audit readiness reviews",
                "Corrective action and ongoing compliance",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-navy/90">
                  <Award size={16} className="text-teal mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-mist">
        <div className="container-x">
          <SectionHeading align="center" eyebrow="Our Values" title="The principles behind our work" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {aboutValues.map((v) => {
              const Icon = iconMap[v.icon] || ShieldCheck;
              return (
                <div key={v.title} className="bg-white rounded-2xl p-6 border border-slate-200 card-soft">
                  <div className="w-12 h-12 rounded-xl bg-teal-soft text-teal flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h4 className="text-navy font-bold text-base">{v.title}</h4>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-20" />
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight">Speak with an audit informed consultant</h2>
            <p className="mt-4 text-slate-300 text-lg max-w-2xl">
              Book a free 15 minute discovery call to discuss your registration pathway, scope and audit timeline.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/contact" className="btn-primary px-6 py-3.5 rounded-lg font-semibold inline-flex items-center gap-2">
              Book Discovery Call <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
